import { stopPlayingYoutube, youtubeSrc } from '../../src/js/youtube';

const SLIDE_MS = 600;
const slideEase = cubicBezier(0.63, 0.05, 0, 1);

function cubicBezier (x1, y1, x2, y2) {
	const cx = 3 * x1;
	const bx = 3 * (x2 - x1) - cx;
	const ax = 1 - cx - bx;
	const cy = 3 * y1;
	const by = 3 * (y2 - y1) - cy;
	const ay = 1 - cy - by;
	const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
	const sampleY = (t) => ((ay * t + by) * t + cy) * t;
	const slopeX = (t) => (3 * ax * t + 2 * bx) * t + cx;
	return (x) => {
		let t = x;
		for (let i = 0; i < 6; i += 1) {
			const delta = sampleX(t) - x;
			const slope = slopeX(t);
			if (Math.abs(delta) < 1e-4 || slope === 0) break;
			t -= delta / slope;
		}
		return sampleY(Math.min(1, Math.max(0, t)));
	};
}

export default function workLive () {
	document.querySelectorAll('.work-live').forEach(bind);
}

function bind (section) {
	const bar = section.querySelector('.nav-cover-bar');
	if (bar) {
		const header = document.querySelector('.site-header');
		const syncBar = () => {
			const box = section.getBoundingClientRect();
			const line = header ? header.getBoundingClientRect().bottom : 0;
			bar.classList.toggle('is-fixed', box.top < line && box.bottom > line);
		};
		syncBar();
		window.addEventListener('scroll', syncBar, { passive: true });
		document.addEventListener('scroll', syncBar, { passive: true, capture: true });
		window.addEventListener('resize', syncBar);
		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver(syncBar, {
				rootMargin: '0px 0px -99% 0px',
				threshold: 0,
			});
			observer.observe(section);
		}
	}

	const track = section.querySelector('.track');
	const tiles = [...section.querySelectorAll('.tile')];
	const dots = [...section.querySelectorAll('.dot')];
	const dataEl = section.querySelector('.live-data');
	const overlay = section.querySelector('.live-overlay');
	if (! track || ! tiles.length || ! dataEl || ! overlay) return;

	let items = [];
	try {
		items = JSON.parse(dataEl.textContent || '[]');
	} catch (error) {
		items = [];
	}

	let active = Math.max(0, tiles.findIndex((tile) => tile.classList.contains('is-active')));
	let cursor = active;
	let openIndex = -1;
	let audio = null;

	const cloneTile = (tile) => {
		const clone = tile.cloneNode(true);
		clone.classList.remove('is-active');
		clone.setAttribute('aria-hidden', 'true');
		clone.tabIndex = -1;
		return clone;
	};

	if (tiles.length > 1) {
		const first = tiles[0];
		tiles.forEach((tile) => track.insertBefore(cloneTile(tile), first));
		tiles.forEach((tile) => track.appendChild(cloneTile(tile)));
		cursor = tiles.length + active;
	}

	const slots = () => [...track.querySelectorAll('.tile')];

	const stage = overlay.querySelector('.stage');
	const caption = overlay.querySelector('.overlay-caption');
	const count = overlay.querySelector('.overlay-count');

	const narrow = window.matchMedia('(width < 768px)');
	const compact = window.matchMedia('(width < 1024px)');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let slideId = 0;

	const collapsedWidth = () => {
		const widths = tiles.map((tile) => tile.getBoundingClientRect().width).filter((width) => width > 0);
		if (! widths.length) return 224;
		return widths.reduce((best, width) => Math.abs(width - 224) < Math.abs(best - 224) ? width : best);
	};

	const frame = () => {
		const styles = getComputedStyle(track);
		const gap = parseFloat(styles.columnGap) || 0;
		const client = track.clientWidth;
		if (narrow.matches || client < 1) {
			section.style.removeProperty('--live-active');
			track.style.paddingLeft = '';
			track.style.paddingRight = '';
			const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
			return { mode: 'narrow', gap, client, pad };
		}
		const activeWidth = compact.matches ? Math.min(920, Math.max(0, client - 80)) : 920;
		const inset = (client - activeWidth) / 2;
		const pad = Math.max(0, inset);
		section.style.setProperty('--live-active', activeWidth + 'px');
		track.style.paddingLeft = pad + 'px';
		track.style.paddingRight = pad + 'px';
		return { mode: 'center', gap, client, activeWidth, inset, pad, collapsed: collapsedWidth() };
	};

	const scrollTarget = () => {
		const tile = slots()[cursor];
		if (! tile) return track.scrollLeft;
		const layout = frame();
		const max = Math.max(0, track.scrollWidth - track.clientWidth);
		if (layout.mode === 'narrow') {
			const target = tile.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - layout.pad;
			return Math.max(0, Math.min(max, target));
		}
		const tileLeft = layout.pad + cursor * (layout.collapsed + layout.gap);
		const target = tileLeft - layout.inset;
		return Math.max(0, Math.min(max, target));
	};

	const settle = () => {
		const count = tiles.length;
		if (count < 2) return;
		const home = count + ((cursor % count) + count) % count;
		if (home === cursor) return;
		const row = slots();
		const from = row[cursor];
		const to = row[home];
		if (! from || ! to) return;
		const seen = from.getBoundingClientRect().left;
		section.classList.add('is-settling');
		void section.offsetWidth;
		row.forEach((tile, index) => tile.classList.toggle('is-active', index === home));
		cursor = home;
		active = home - count;
		void track.offsetWidth;
		track.scrollLeft += to.getBoundingClientRect().left - seen;
		void track.offsetWidth;
		section.classList.remove('is-settling');
	};

	const place = (smooth) => {
		const destination = scrollTarget();
		const start = track.scrollLeft;
		slideId += 1;
		const run = slideId;
		const finish = () => {
			if (run !== slideId) return;
			settle();
		};
		if (! smooth || reduceMotion.matches || Math.abs(destination - start) < 1) {
			track.scrollLeft = destination;
			finish();
			return;
		}
		const began = performance.now();
		const step = (now) => {
			if (run !== slideId) return;
			const progress = Math.min(1, (now - began) / SLIDE_MS);
			track.scrollLeft = start + (destination - start) * slideEase(progress);
			if (progress < 1) requestAnimationFrame(step);
			else finish();
		};
		requestAnimationFrame(step);
	};

	const paint = (smooth) => {
		slots().forEach((tile, index) => tile.classList.toggle('is-active', index === cursor));
		dots.forEach((dot, index) => {
			const on = index === active;
			dot.classList.toggle('is-active', on);
			if (on) dot.setAttribute('aria-current', 'true');
			else dot.removeAttribute('aria-current');
		});
		const prev = section.querySelector('.live-prev');
		const next = section.querySelector('.live-next');
		const single = tiles.length < 2;
		if (prev) prev.disabled = single;
		if (next) next.disabled = single;
		place(smooth);
	};

	const go = (index) => {
		const count = tiles.length;
		if (! count) return;
		const logical = (index % count + count) % count;
		if (count < 2) {
			if (logical === active) return;
			active = logical;
			cursor = logical;
			paint(true);
			return;
		}
		const candidates = [logical, count + logical, count * 2 + logical];
		const nextCursor = candidates.reduce((best, slot) => Math.abs(slot - cursor) < Math.abs(best - cursor) ? slot : best);
		if (nextCursor === cursor) return;
		cursor = nextCursor;
		active = logical;
		paint(true);
	};

	const stop = () => {
		if (audio) {
			audio.pause();
			audio = null;
		}
		stage.innerHTML = '';
		stage.className = 'stage';
	};

	const clock = (seconds) => {
		const value = Math.max(0, Math.floor(seconds || 0));
		const minutes = Math.floor(value / 60);
		const rest = String(value % 60).padStart(2, '0');
		return minutes + ':' + rest;
	};

	const show = (index) => {
		const item = items[index];
		if (! item) return;
		openIndex = index;
		go(index);
		if (item.kind === 'video' && item.youtube) stopPlayingYoutube();
		stop();
		overlay.hidden = false;
		overlay.classList.add('is-open');
		document.body.style.overflow = 'hidden';
		const label = item.client && item.title ? item.client + ': ' + item.title : (item.title || item.client || '');
		caption.textContent = label;
		count.textContent = String(index + 1).padStart(2, '0') + '/' + String(items.length).padStart(2, '0');

		if (item.kind === 'video' && item.youtube) {
			stage.classList.add('is-video');
			const frame = document.createElement('iframe');
			frame.className = 'frame';
			frame.src = youtubeSrc(item.youtube);
			frame.title = label || 'Video';
			frame.allow = 'autoplay; encrypted-media; picture-in-picture';
			frame.allowFullscreen = true;
			stage.append(frame);
		} else if (item.kind === 'audio' && item.audio) {
			stage.classList.add('is-audio');
			if (item.image) {
				const img = document.createElement('img');
				img.className = 'player';
				img.alt = '';
				img.src = item.image;
				stage.append(img);
			}
			const veil = document.createElement('div');
			veil.className = 'veil';
			stage.append(veil);
			const wave = document.createElement('div');
			wave.className = 'wave';
			wave.setAttribute('aria-hidden', 'true');
			for (let bar = 0; bar < 24; bar += 1) {
				const span = document.createElement('span');
				span.style.animationDelay = (bar % 8) * 0.08 + 's';
				wave.append(span);
			}
			stage.append(wave);
			audio = document.createElement('audio');
			audio.src = item.audio;
			audio.preload = 'metadata';
			stage.append(audio);
			const ui = document.createElement('div');
			ui.className = 'audio-ui';
			ui.innerHTML = '<button type="button" class="scrub" aria-label="Seek"><span class="scrub-fill"></span></button><div class="audio-row"><button type="button" class="audio-btn audio-play" aria-label="Play">Play</button><button type="button" class="audio-btn is-ghost audio-mute" aria-label="Mute">Mute</button><span class="audio-time">0:00 / 0:00</span></div>';
			stage.append(ui);
			const play = ui.querySelector('.audio-play');
			const mute = ui.querySelector('.audio-mute');
			const time = ui.querySelector('.audio-time');
			const scrub = ui.querySelector('.scrub');
			const fill = ui.querySelector('.scrub-fill');
			const sync = () => {
				const duration = audio.duration || 0;
				const ratio = duration ? (audio.currentTime / duration) * 100 : 0;
				fill.style.width = ratio + '%';
				time.textContent = clock(audio.currentTime) + ' / ' + clock(duration);
				stage.classList.toggle('is-paused', audio.paused);
				play.textContent = audio.paused ? 'Play' : 'Pause';
				play.setAttribute('aria-label', audio.paused ? 'Play' : 'Pause');
			};
			play.addEventListener('click', () => {
				if (audio.paused) audio.play();
				else audio.pause();
			});
			mute.addEventListener('click', () => {
				audio.muted = ! audio.muted;
				mute.setAttribute('aria-label', audio.muted ? 'Unmute' : 'Mute');
			});
			scrub.addEventListener('click', (event) => {
				const box = scrub.getBoundingClientRect();
				const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
				if (audio.duration) audio.currentTime = ratio * audio.duration;
			});
			audio.addEventListener('timeupdate', sync);
			audio.addEventListener('loadedmetadata', sync);
			audio.addEventListener('play', sync);
			audio.addEventListener('pause', sync);
			stage.classList.add('is-paused');
			audio.play().catch(() => {});
		} else if (item.lightbox || item.image) {
			stage.classList.add('is-image');
			const img = document.createElement('img');
			img.className = 'frame';
			img.alt = label;
			img.src = item.lightbox || item.image;
			stage.append(img);
		}

		overlay.querySelector('.overlay-close').focus();
	};

	const close = () => {
		stop();
		openIndex = -1;
		overlay.classList.remove('is-open');
		overlay.hidden = true;
		document.body.style.overflow = '';
	};

	track.addEventListener('click', (event) => {
		const tile = event.target.closest('.tile');
		if (! tile || ! track.contains(tile)) return;
		show(Number(tile.dataset.index));
	});
	dots.forEach((dot) => {
		dot.addEventListener('click', () => go(Number(dot.dataset.index)));
	});
	section.querySelector('.live-prev')?.addEventListener('click', () => go(active - 1));
	section.querySelector('.live-next')?.addEventListener('click', () => go(active + 1));
	overlay.querySelector('.overlay-prev')?.addEventListener('click', () => show((openIndex - 1 + items.length) % items.length));
	overlay.querySelector('.overlay-next')?.addEventListener('click', () => show((openIndex + 1) % items.length));
	overlay.querySelector('.overlay-close')?.addEventListener('click', close);
	overlay.addEventListener('click', (event) => {
		if (event.target === overlay) close();
	});
	document.addEventListener('keydown', (event) => {
		if (overlay.hidden) return;
		if (event.key === 'Escape') close();
		if (event.key === 'ArrowLeft') show((openIndex - 1 + items.length) % items.length);
		if (event.key === 'ArrowRight') show((openIndex + 1) % items.length);
	});

	window.addEventListener('resize', () => place(false));
	paint();
}
